/**
 * Minimal SSE block parser for `POST /chat/message/stream`.
 * Pure helpers — no network I/O (easy to unit test).
 */

export interface ParsedSseEvent {
  event: string;
  data: string;
}

/**
 * Parse one SSE event block (text between blank-line separators).
 * Supports multi-line `data:` fields per the SSE spec.
 */
export function parseSseBlock(block: string): ParsedSseEvent | null {
  const trimmed = block.trim();
  if (!trimmed) return null;

  let event = 'message';
  const dataLines: string[] = [];

  for (const rawLine of trimmed.split('\n')) {
    const line = rawLine.replace(/\r$/, '');
    if (!line || line.startsWith(':')) continue;
    if (line.startsWith('event:')) {
      event = line.slice(6).trim();
      continue;
    }
    if (line.startsWith('data:')) {
      // Spec: optional single space after the colon.
      dataLines.push(line.slice(5).replace(/^ /, ''));
    }
  }

  if (dataLines.length === 0) return null;
  return { event, data: dataLines.join('\n') };
}

/**
 * Consume complete SSE events from a rolling text buffer.
 * Incomplete trailing bytes stay in `rest` for the next chunk.
 */
export function consumeSseBuffer(buffer: string): {
  events: ParsedSseEvent[];
  rest: string;
} {
  const normalized = buffer.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const parts = normalized.split('\n\n');
  const rest = parts.pop() ?? '';
  const events: ParsedSseEvent[] = [];

  for (const part of parts) {
    const parsed = parseSseBlock(part);
    if (parsed) events.push(parsed);
  }

  return { events, rest };
}
